import { useState } from 'react';
import { Badge } from 'react-bootstrap';
import { FaDownload, FaPrint } from 'react-icons/fa';
import DataTable from 'react-data-table-component';
import LayoutFilter from '../../data/LayoutFilter';
import LayoutTable from '../../data/LayoutTable';
import ReactSelect from '../../data/reactSelect';
import { InputDefault } from '../../data/inputDefault';
import tableStyleDefault from '../../../Services/tableStyleDefault';
import CustomLoader from '../../data/customLoader';
import { paginationOptions } from '../../data/PagineteInfo';
import { IListNfeComponent, ISalesNfeItem, ISalesSatItem } from '../../../interfaces/INfeList';
import { useQueryFetchPages } from '../../../apiQuery/useQueryFetchPages';
import { useAppSelector } from '../../../redux/store';
import Globals from '../../../Globals.json';
import * as S from './styled';

const NULL_DATE_YEAR = 1900;

type TipoNota = 'todas' | 'nfce' | 'sat' | 'nfe';

const tipoOptions = [
  { value: 'todas', label: 'Todas (NFC-e / NF-e)' },
  { value: 'nfce', label: 'NFC-e' },
  { value: 'sat', label: 'SAT' },
  { value: 'nfe', label: 'NF-e' },
];

const documentTypeByTipo: Record<string, number> = { nfce: 65, nfe: 55 };

const isValidDate = (date?: string | null) =>
  !!date && new Date(date).getFullYear() > NULL_DATE_YEAR;

const formatDate = (date?: string | null) =>
  isValidDate(date) ? new Date(date as string).toLocaleString('pt-BR') : '---';

const formatCurrency = (value: string | number | null) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value || 0)
  );

const isUrl = (value?: string | null) => !!value && /^https?:\/\//i.test(value);

export default function ListNotasFiscais({ title, onEmitir }: IListNfeComponent) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  const storeId = storeMe?.storeActive?.id;

  const [page, setPage] = useState(1);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [tipo, setTipo] = useState<TipoNota>('todas');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  const isSat = tipo === 'sat';

  const queryParams = isSat
    ? {
        'filter[store_id]': storeId,
        ...(dataInicio && { 'filter[created_from]': dataInicio }),
        ...(dataFim && { 'filter[created_until]': dataFim }),
      }
    : {
        'filter[store_id]': storeId,
        ...(documentTypeByTipo[tipo] && { 'filter[DocumentType]': documentTypeByTipo[tipo] }),
        ...(dataInicio && { 'filter[data_inicio]': dataInicio }),
        ...(dataFim && { 'filter[data_fim]': dataFim }),
      };

  const { data: notasData, isLoading } = useQueryFetchPages<ISalesNfeItem | ISalesSatItem>(
    ['notas-fiscais', tipo],
    isSat ? Globals.api.satListar : Globals.api.nfeListar,
    { page, queryParams, enabled: !!storeId }
  );

  const handleChangeTipo = (value: any) => {
    setTipo((value || 'todas') as TipoNota);
    setPage(1);
  };

  const xmlLink = (href: string) => (
    <a
      className="btn btn-sm btn-outline-success"
      href={href}
      target="_blank"
      rel="noreferrer"
      title="Baixar XML"
    >
      <FaDownload size={12} />
    </a>
  );

  const nfeColumns = [
    {
      name: 'Nº Recibo',
      center: true,
      grow: 1,
      selector: (row: ISalesNfeItem) => row.receiptnumber || '---',
    },
    {
      name: 'Tipo',
      center: true,
      grow: 1,
      selector: (row: ISalesNfeItem) => (row.DocumentType === 55 ? 'NF-e' : 'NFC-e'),
    },
    {
      name: 'Chave de Acesso',
      grow: 3,
      cell: (row: ISalesNfeItem) =>
        row.accesskey ? (
          <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>{row.accesskey}</span>
        ) : (
          '---'
        ),
    },
    {
      name: 'Status',
      center: true,
      grow: 1,
      cell: (row: ISalesNfeItem) =>
        isValidDate(row.cancelationdate) ? (
          <Badge bg="secondary">Cancelada</Badge>
        ) : (
          <Badge bg="success">Status {row.status}</Badge>
        ),
    },
    {
      name: 'Entrega',
      center: true,
      grow: 2,
      selector: (row: ISalesNfeItem) => formatDate(row.deliverydate),
    },
    {
      name: 'Criada em',
      center: true,
      grow: 2,
      selector: (row: ISalesNfeItem) => formatDate(row.created_at),
    },
    {
      name: 'Imprimir',
      center: true,
      grow: 1,
      cell: (row: ISalesNfeItem) =>
        isUrl(row.qrcode) ? (
          <a
            className="btn btn-sm btn-outline-primary"
            href={row.qrcode as string}
            target="_blank"
            rel="noreferrer"
            title="Imprimir DANFE"
          >
            <FaPrint size={12} />
          </a>
        ) : (
          '---'
        ),
      button: true,
    },
  ];

  const satColumns = [
    {
      name: 'Venda',
      center: true,
      grow: 1,
      selector: (row: ISalesSatItem) => row.sale_id ?? '---',
    },
    {
      name: 'Chave de Acesso',
      grow: 3,
      cell: (row: ISalesSatItem) =>
        row.accesskey ? (
          <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>{row.accesskey}</span>
        ) : (
          '---'
        ),
    },
    {
      name: 'CPF/CNPJ',
      center: true,
      grow: 2,
      selector: (row: ISalesSatItem) => row.cnpjcpf || '---',
    },
    {
      name: 'Total',
      center: true,
      grow: 1,
      cell: (row: ISalesSatItem) => formatCurrency(row.total),
    },
    {
      name: 'Status',
      center: true,
      grow: 1,
      cell: (row: ISalesSatItem) =>
        row.canceled ? <Badge bg="secondary">Cancelado</Badge> : <Badge bg="success">Emitido</Badge>,
    },
    {
      name: 'Data da Venda',
      center: true,
      grow: 2,
      selector: (row: ISalesSatItem) => formatDate(row.saledate),
    },
    {
      name: 'Arquivos',
      center: true,
      grow: 1,
      cell: (row: ISalesSatItem) =>
        row.xml_file_url ? <div className="actions flex gap-2">{xmlLink(row.xml_file_url)}</div> : '---',
      button: true,
    },
  ];

  const handleAdd = () => {
    onEmitir();
  };

  const onClickOpenFilter = () => {
    setOpenFilter(!openFilter);
  };

  const filterFields = (
    <>
      <ReactSelect
        label="Tipo de Nota"
        isClearable={false}
        placeholder="Tipo de nota"
        options={tipoOptions}
        state={tipo}
        setState={handleChangeTipo}
      />

      <InputDefault
        label="Data Início"
        type="date"
        state={dataInicio}
        setState={(value: any) => {
          setDataInicio(value);
          setPage(1);
        }}
        placeholder="Data início"
      />

      <InputDefault
        label="Data Fim"
        type="date"
        state={dataFim}
        setState={(value: any) => {
          setDataFim(value);
          setPage(1);
        }}
        placeholder="Data fim"
      />
    </>
  );

  return (
    <>
      <LayoutFilter
        title={title}
        onClickAdd={handleAdd}
        onClickOpenFilter={onClickOpenFilter}
        openFilter={openFilter}
      >
        <S.Wrapper>
          {openFilter && <div className="min-screen">{filterFields}</div>}
          <div className="max-screen">{filterFields}</div>
        </S.Wrapper>
      </LayoutFilter>

      <LayoutTable>
        <DataTable
          columns={(isSat ? satColumns : nfeColumns) as any}
          data={notasData?.data || []}
          striped
          pagination
          paginationServer
          paginationTotalRows={notasData?.meta?.total || 0}
          paginationDefaultPage={1}
          paginationPerPage={20}
          onChangePage={setPage}
          onChangeRowsPerPage={(_newPerPage: number, newPage: number) => setPage(newPage)}
          customStyles={tableStyleDefault}
          paginationComponentOptions={paginationOptions}
          progressPending={isLoading}
          progressComponent={<CustomLoader />}
          noDataComponent="Nenhuma nota fiscal foi encontrada."
        />
      </LayoutTable>
    </>
  );
}
